import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, CalendarDays, Users } from "lucide-react";
import { FadeUp } from "./Motion";

const ProjectRow = ({ p, d, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = i % 2 === 1;

  return (
    <article ref={ref} data-testid={`project-${p.id}`} className="relative grid gap-8 py-14 pl-8 lg:grid-cols-12 lg:gap-12 lg:py-20 lg:pl-0">
      <span className="absolute left-0 top-16 h-3 w-3 -translate-x-[5px] rounded-full bg-[#C85A32] ring-8 ring-[#FBF9F5] lg:left-1/2 lg:top-24 lg:-translate-x-1/2" />
      <FadeUp className={`lg:col-span-6 ${flip ? "lg:order-2 lg:col-start-7 lg:pl-10" : "lg:pr-10"}`}>
        <div className="group relative aspect-[4/3] overflow-hidden rounded-sm bg-[#EFEAE1]">
          <motion.img style={{ y }} src={p.img} alt={p.title} className="absolute inset-0 h-[116%] w-full -translate-y-[8%] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
          <span className="absolute left-4 top-4 rounded-full bg-[#FBF9F5]/90 px-3 py-1 font-mono text-xs text-[#1C1917] backdrop-blur">{p.year}</span>
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className={`lg:col-span-6 lg:self-center ${flip ? "lg:order-1 lg:pr-16" : "lg:pl-16"}`}>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#3B5E4C]">{String(i + 1).padStart(2, "0")} · {d.type}</span>
        <h3 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight text-[#1C1917] sm:text-5xl">{p.title}</h3>
        <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-700">
          <li className="flex items-center gap-2"><MapPin size={15} className="text-[#C85A32]" />{d.place}</li>
          <li className="flex items-center gap-2"><CalendarDays size={15} className="text-[#C85A32]" />{d.date}</li>
        </ul>
        <p className="mt-6 text-base leading-relaxed text-stone-600 sm:text-lg">{d.text}</p>
        <p className="mt-6 inline-flex items-center gap-2 border-t border-[#D6CEBF] pt-4 text-sm text-stone-700"><Users size={15} className="text-[#3B5E4C]" />{d.meta}</p>
      </FadeUp>
    </article>
  );
};

export const ProjectTimeline = ({ projects, lang }) => (
  <div data-testid="project-timeline" className="relative">
    <div className="absolute bottom-0 left-0 top-0 w-px bg-[#D6CEBF] lg:left-1/2" />
    {projects.map((p, i) => <ProjectRow key={p.id} p={p} d={p[lang]} i={i} />)}
  </div>
);
